import { ArrowRight } from 'lucide-react';

import { useT } from '../../../../i18n';
import { useDichCaiDat } from '../../hooks/useDichCaiDat';
import { soSanhQuyTac } from '../../nghiep-vu/quy-tac-canh-bao';
import type { DongCidr, QuyTacCanhBao, SettingDefinition } from '../../types';

/**
 * Một thay đổi dạng "trước → sau", dùng chung cho hộp xác nhận và lịch sử.
 *
 * Danh sách CIDR không in hai danh sách đầy đủ cạnh nhau — 200 dòng so với
 * 199 dòng thì không ai tìm ra dòng khác. Thay vào đó nói **thêm** gì, **bỏ** gì.
 */
export function GiaTriThayDoi({
  def,
  truoc,
  sau,
}: {
  def: SettingDefinition | undefined;
  truoc: unknown;
  sau: unknown;
}) {
  const { t } = useT();
  const d = useDichCaiDat();

  if (def?.type === 'cidr_list') {
    const a = new Set(((truoc as DongCidr[] | null) ?? []).map((x) => x.cidr));
    const b = new Set(((sau as DongCidr[] | null) ?? []).map((x) => x.cidr));
    const them = [...b].filter((c) => !a.has(c));
    const bo = [...a].filter((c) => !b.has(c));
    return (
      <div className="space-y-1 text-xs">
        {them.length > 0 && (
          <p className="break-all text-content-primary">
            <span className="font-medium text-success-700">{t('caiDat.thayDoi.them')}</span>{' '}
            <span className="font-mono">{them.join(', ')}</span>
          </p>
        )}
        {bo.length > 0 && (
          <p className="break-all text-content-primary">
            <span className="font-medium text-danger-700">{t('caiDat.thayDoi.bo')}</span>{' '}
            <span className="font-mono">{bo.join(', ')}</span>
          </p>
        )}
        {them.length === 0 && bo.length === 0 && (
          <p className="text-content-muted">{t('caiDat.thayDoi.chiDoiGhiChu')}</p>
        )}
      </div>
    );
  }

  if (def?.type === 'alert_rule_list') {
    const { them, bo, sua } = soSanhQuyTac(truoc as QuyTacCanhBao[] | null, sau as QuyTacCanhBao[] | null);
    const ten = (ds: QuyTacCanhBao[]) => ds.map((q) => q.name).join(', ');
    return (
      <div className="space-y-1 text-xs">
        {them.length > 0 && (
          <p className="break-words text-content-primary">
            <span className="font-medium text-success-700">{t('caiDat.thayDoi.them')}</span> {ten(them)}
          </p>
        )}
        {sua.length > 0 && (
          <p className="break-words text-content-primary">
            <span className="font-medium text-warning-700">{t('caiDat.thayDoi.sua')}</span> {ten(sua)}
          </p>
        )}
        {bo.length > 0 && (
          <p className="break-words text-content-primary">
            <span className="font-medium text-danger-700">{t('caiDat.thayDoi.bo')}</span> {ten(bo)}
          </p>
        )}
        {them.length === 0 && sua.length === 0 && bo.length === 0 && (
          <p className="text-content-muted">{t('caiDat.thayDoi.chiDoiThuTu')}</p>
        )}
      </div>
    );
  }

  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="break-words text-content-muted line-through">{d.giaTri(def, truoc)}</span>
      <ArrowRight size={12} className="shrink-0 text-content-muted" aria-label={t('caiDat.thayDoi.thanh')} />
      <span className="break-words font-medium text-content-primary">{d.giaTri(def, sau)}</span>
    </p>
  );
}
